import { apiClient } from './client';
import type {
	Organization,
	OrganizationCreate,
	OrganizationUpdate,
	OrganizationWithMembers,
	OrganizationMember,
	OrganizationStats
} from './types';

export class OrganizationsApi {
	// Get user's organizations (where they are owner or member)
	async getUserOrganizations(): Promise<Organization[]> {
		return apiClient.get<Organization[]>('/organizations');
	}

	// Get organizations owned by the user
	async getOwnedOrganizations(): Promise<Organization[]> {
		return apiClient.get<Organization[]>('/organizations/owned');
	}

	// Get a specific organization by ID
	async getOrganization(id: string): Promise<Organization> {
		return apiClient.get<Organization>(`/organizations/${id}`);
	}

	// Get organization with its members
	async getOrganizationWithMembers(id: string): Promise<OrganizationWithMembers> {
		return apiClient.get<OrganizationWithMembers>(`/organizations/${id}/members`);
	}

	// Get organization members list
	async getOrganizationMembers(id: string): Promise<OrganizationMember[]> {
		return apiClient.get<OrganizationMember[]>(`/organizations/${id}/members/list`);
	}

	// Get organization statistics
	async getOrganizationStats(id: string): Promise<OrganizationStats> {
		return apiClient.get<OrganizationStats>(`/organizations/${id}/stats`);
	}

	// Create a new organization
	async createOrganization(organization: OrganizationCreate): Promise<Organization> {
		return apiClient.post<Organization>('/organizations', organization);
	}

	// Update an organization (owner only)
	async updateOrganization(id: string, organization: OrganizationUpdate): Promise<Organization> {
		return apiClient.put<Organization>(`/organizations/${id}`, organization);
	}

	// Delete an organization (owner only)
	async deleteOrganization(id: string): Promise<void> {
		return apiClient.delete<void>(`/organizations/${id}`);
	}

	// Add a member to the organization
	async addMember(organizationId: string, userId: string, role: string = 'member'): Promise<void> {
		return apiClient.post<void>(`/organizations/${organizationId}/members`, {
			userId,
			role
		});
	}

	// Update a member's role
	async updateMemberRole(organizationId: string, userId: string, role: string): Promise<void> {
		return apiClient.put<void>(`/organizations/${organizationId}/members/${userId}`, {
			role
		});
	}

	// Remove a member from the organization
	async removeMember(organizationId: string, userId: string): Promise<void> {
		return apiClient.delete<void>(`/organizations/${organizationId}/members/${userId}`);
	}
}

export const organizationsApi = new OrganizationsApi();
